import * as moment from "moment";
import { Country } from "./../models/country";
import { Injectable } from "@angular/core";
import { ApiService } from "./api.service";
import { ProgramArticle } from "../models/program-article";
import { Value } from "../models/value";
import { FiltersStateInterface } from "../store/actions/filters.actions";
import { Language } from "../models/language";
import { ProgramArticleStats } from "../models/program-article-stats";
import { ChartModel } from "../models/chart-model";
import {
  ProgramArticleTranslationETTValue,
  ProgramArticlePictureETTValue,
} from "../models/program-article-translation-value";
import { PictureSets } from "../models/pictureSets";
import { additionalPicture } from "../models/additionalPicture";
import { ProgramArticlePictureRequest } from "../models/programArticlePictureRequest";
import { additionalPictureRequest } from "../models/additionalPictureRequest";

@Injectable()
export class ProgramArticleService {
  /**
   * Load dependencies
   * @param {ApiService} apiService
   */
  constructor(private apiService: ApiService) {}

  /**
   * Search program articles with parameters from the API
   * @param {FiltersStateInterface} filters
   * @param {Language} language
   * @returns {Observable<any>}
   */
  getProgramArticles(filters: FiltersStateInterface, language: Language) {
    const data = {
      languageId: language.id,
      startTimestamp: filters.startDate
        ? this.getTimestampStartDate(filters.midnightMode, filters.startDate)
        : null,
      endTimestamp: filters.endDate
        ? this.getTimestampEndDate(filters.midnightMode, filters.endDate)
        : null,
      channels: filters.channels,
      keywords: filters.keywords,
      referentialEntities: filters.referentialEntities,
      edited: filters.edited,
      idValue: filters.idValue,
      filterDestination: filters.filterDestination,
      broadcastingCountries: filters.broadcastingCountries,
      field: filters.field,
      fieldStatus: filters.fieldStatus,
      idType: filters.idType,
      defaultDestination: filters.defaultDestination,
      updatedInTheLastHours: filters.updatedInTheLastHours,
      showHidden: filters.showHidden,
    };
    return this.apiService.post("ProgramArticle/Search", data).map((res) => {
      if (res && res.values) {
        return res.values.map((value) => new ProgramArticle(value));
      } else {
        return [];
      }
    });
  }

  /**
   * Fetch a program article by id from the API
   * @param {number} valEmiId
   * @param {number} channelId
   * @param {number} languageId
   * @param {number} cocoonId
   * @returns {Observable<ProgramArticle>}
   */
  getProgramArticle(
    valEmiId: number,
    channelId: number,
    languageId: number,
    cocoonId: number
  ) {
    return this.apiService
      .get(
        "ProgramArticle/" +
          valEmiId +
          "/" +
          channelId +
          "/" +
          languageId +
          "/" +
          cocoonId
      )
      .map((res) => {
        if (res && res.value) {
          return new ProgramArticle(res.value);
        }
      });
  }

  /**
   * Fetch all referential types from the API
   * @param {boolean} isNonSportiveClassif
   * @returns {Observable<any>}
   */
  getReferentialTypes(isNonSportiveClassif: boolean) {
    return this.apiService
      .get("ProgramArticle/GetReferentialTypes/" + isNonSportiveClassif)
      .map((response) => {
        if (response && response.values) {
          return response.values.map((value) => new Value(value));
        } else {
          return [];
        }
      });
  }

  /**
   * Fetch all fields status from the API
   * @returns {Observable<any>}
   */
  GetFieldsStatus() {
    return this.apiService
      .get("ProgramArticle/GetFieldsStatus")
      .map((response) => {
        if (response && response.values) {
          return response.values.map((value) => new Value(value));
        } else {
          return [];
        }
      });
  }

  /**
   * Fetch all fields from the API
   * @returns {Observable<any>}
   */
  GetFields() {
    return this.apiService.get("ProgramArticle/GetFields").map((response) => {
      if (response && response.values) {
        return response.values.map((value) => new Value(value));
      } else {
        return [];
      }
    });
  }

  /**
   * Fetch all Id Types from the API
   * @returns {Observable<any>}
   */
  GetIdTypes() {
    return this.apiService.get("ProgramArticle/GetIdTypes").map((response) => {
      if (response && response.values) {
        return response.values.map((value) => new Value(value));
      } else {
        return [];
      }
    });
  }

  /**
   * Fetch all affiliates from the API
   * @returns {Observable<any>}
   */
  GetAffiliates() {
    return this.apiService.get("ProgramArticle/Affiliates").map((response) => {
      if (response && response.values) {
        return response.values.map((value) => new Value(value));
      } else {
        return [];
      }
    });
  }

  /**
   * Fetch all tierings from the API
   * @returns {Observable<any>}
   */
  GetTierings() {
    return this.apiService.get("ProgramArticle/Tierings").map((response) => {
      if (response && response.values) {
        return response.values.map((value) => new Value(value));
      } else {
        return [];
      }
    });
  }

  /**
   * Update pictures with an API call
   * @param {ProgramArticle} programArticle
   * @returns {Observable<any>}
   */
  updatePictures(programArticle: ProgramArticle, saveReplays: boolean = false) {
    programArticle.cleanPicture(false);

    return this.apiService.post(
      "ProgramArticle/UploadPictures?saveReplays=" + saveReplays,
      programArticle
    );
  }

  /**
   * Update pictures with an API call
   * @param {ProgramArticle} programArticle
   * @returns {Observable<any>}
   */
  uploadAdditionalPictures(
    programArticle: PictureSets,
    saveReplays: boolean = false
  ) {
    return this.apiService.post(
      "ProgramArticle/UploadAdditionalPictures?saveReplays=" + saveReplays,
      programArticle
    );
  }

  /**
   * Update a program article with an API call
   * @param {ProgramArticle} programArticle
   * @returns {Observable<ProgramArticle>}
   */
  updateProgramArticle(
    programArticle: ProgramArticle,
    saveReplays: boolean = false,
    cleanPicture: boolean = true
  ) {
    if (cleanPicture) programArticle.cleanPicture(true);
    return this.apiService
      .post("ProgramArticle/Save?saveReplays=" + saveReplays, programArticle)
      .map((res) => {
        if (res && res.value) {
          let programs = [];
          const program = new ProgramArticle(res.value);
          if (res.values) {
            programs = res.values.map((value) => new ProgramArticle(value));
          }
          return {
            programArticle: program,
            programArticles: programs,
          };
        }
      });
  }

  /**
   * Fetch all destination from the API
   * @returns {Observable<any>}
   */
  getDestinations(includesAll: boolean) {
    return this.apiService
      .get("ProgramArticle/GetDestinations?includesAll=" + includesAll)
      .map((res) => {
        if (res && res.values) {
          return res.values.map((value) => new Value(value));
        }
      });
  }

  /**
   * Fetch all rights for a Program with transmissionId
   * @returns {Observable<any>}
   */
  getTransmissionMetadatas(transmissionId: string, languageId: number) {
    return this.apiService
      .get(
        "ExternalAPI/Transmission/Metadatas/" +
          transmissionId +
          "/" +
          languageId
      )
      .map((res) => {
        if (res && res.value) {
          return res.value;
        }
      });
  }

  /**
   * Fetch all commentators for a Program with transmissionId
   * @returns {Observable<any>}
   */
  getCommentator(spevId: number, variantId: number, languageName: string) {
    return this.apiService
      .get(
        "ProgramArticle/Commentators/" +
          spevId +
          "/" +
          variantId +
          "/" +
          languageName
      )
      .map((res) => {
        if (res && res.value) {
          return res.value;
        }
      });
  }

  /**
   * Fetch Master Sporting Event Id from chapca
   * @returns {Observable<any>}
   */
  GetMasterSportingEventId(spevId: number) {
    return this.apiService
      .get("ExternalAPI/MasterSportingEvent/" + spevId)
      .map((res) => {
        if (res && res.value) {
          return res.value;
        }
      });
  }

  /**
   * Fetch Sporting Event from chapca
   * @returns {Observable<any>}
   */
  GetSportingEvent(spevId: number) {
    return this.apiService
      .get("ExternalAPI/SportingEvent/" + spevId)
      .map((res) => {
        if (res && res.value) {
          return res.value;
        }
      });
  }

  /**
   * Fetch Sporting Event Metadatas from chapca
   * @returns {Observable<any>}
   */
  GetSportingEventMetadatas(spevId: number, languageId: number) {
    return this.apiService
      .get("ExternalAPI/SportingEvent/Metadatas/" + spevId + "/" + languageId)
      .map((res) => {
        if (res && res.value) {
          return res.value;
        }
      });
  }

  /**
   * Get extract file fromt the API
   * @param {string} format
   * @param {string} destination
   * @param {FiltersStateInterface} filters
   * @returns {Observable<any>}
   */
  getExtract(
    format: string,
    destination: string,
    filters: FiltersStateInterface,
    languageId: number
  ) {
    const criterias = {};
    Object.assign(criterias, filters, { languageId: languageId });

    if (criterias["startDate"]) {
      criterias["startTimestamp"] = this.getTimestampStartDate(
        filters.midnightMode,
        criterias["startDate"]
      );
    }
    if (criterias["endDate"]) {
      criterias["endTimestamp"] = this.getTimestampEndDate(
        filters.midnightMode,
        criterias["endDate"]
      );
    }
    return this.apiService.post(
      "ProgramArticle/Extract?format=" + format + "&destination=" + destination,
      criterias
    );
  }

  getMultilingual(valEmiId: number, destination: string, channelId: number) {
    const url =
      "ProgramArticle/Multi/" +
      valEmiId +
      "?destination=" +
      destination +
      "&channelId=" +
      channelId;
    return this.apiService.get(url).map((res) => {
      if (res && res.value && res.value.translations.length > 0) {
        // receive both translations and pictures
        let result = {
          translations: res.value.translations.map(
            (t) => new ProgramArticleTranslationETTValue(t)
          ),
          pictures: res.value.pictures.map(
            (p) => new ProgramArticlePictureETTValue(p)
          ),
        };
        return result;
      }
    });
  }

  getReplays(
    spevId: number,
    contentId: string,
    languageId: number,
    productCode: string,
    programId: number
  ) {
    contentId = contentId && contentId !== "undefined" ? contentId : "0";
    const url =
      "ProgramArticle/Replays/" +
      spevId +
      "/" +
      contentId +
      "/" +
      productCode +
      "?languageId=" +
      languageId +
      "&programId=" +
      programId;
    return this.apiService.get(url).map((res) => {
      if (res && res.value) {
        return res.value.map((value) => new ProgramArticle(value));
      } else {
        return [];
      }
    });
  }

  getMultiCamInfos(spevId: number, languageId: number) {
    const url = "ProgramArticle/MultiCams/" + spevId + "/" + languageId;
    return this.apiService.get(url).map((res) => {
      if (res && res.value) {
        return res.value.map((value) => new ProgramArticle(value));
      } else {
        return [];
      }
    });
  }

  getPictureSets(
    validatedEmissionId: number,
    channelId: number,
    languageId: number,
    destinationName: string,
    setId?: number
  ) {
    const url =
      "ProgramArticle/" +
      validatedEmissionId +
      "/" +
      channelId +
      "/" +
      languageId +
      "/" +
      destinationName +
      "/" +
      setId +
      "/PictureSets";
    return this.apiService.get(url).map((res) => {
      if (res && res.value) {
        return new PictureSets(res.value);
      } else {
        return [];
      }
    });
  }

  getBroadcastingCountries(languageId: number) {
    const url =
      "ProgramArticle/GetBroadcastingCountries" + "?LanguageId=" + languageId;
    return this.apiService.get(url).map((res) => {
      if (res && res.values) {
        return res.values.map((value) => new Country(value));
      } else {
        return [];
      }
    });
  }

  getStats(
    channelId: number,
    languageId: number,
    startDate: Date,
    endDate: Date,
    midnightMode = false
  ) {
    const start = startDate
      ? this.getTimestampStartDate(midnightMode, startDate)
      : null;
    const end = endDate
      ? this.getTimestampEndDate(midnightMode, endDate)
      : null;
    const url =
      "ProgramArticle/Stats" +
      "?channelId=" +
      channelId +
      "&languageId=" +
      languageId +
      "&startTimestamp=" +
      start +
      "&endTimestamp=" +
      end;
    return this.apiService.get(url).map((res) => {
      if (res && res.values) {
        const results: ProgramArticleStats[] = [];
        res.values.forEach((value) => {
          const stats = new ProgramArticleStats();
          stats.destination = value.destination;
          value.fields.forEach((field) => {
            stats.charts.push(
              new ChartModel({
                title: field.name,
                labels: field.labels,
                data: field.datas,
                type: "pie",
                isDisplayed: true,
              })
            );
          });
          results.push(stats);
        });
        return results;
      } else {
        return [];
      }
    });
  }

  getTimestampStartDate(midnightMode: boolean, date: Date) {
    return midnightMode
      ? moment(date)
          .hours(1)
          .minute(0)
          .second(0)
          .millisecond(0)
          .toDate()
          .getTime() / 1000
      : moment(date)
          .hours(4)
          .minute(0)
          .second(0)
          .millisecond(0)
          .toDate()
          .getTime() / 1000;
  }

  getTimestampEndDate(midnightMode: boolean, date: Date) {
    return midnightMode
      ? moment(date)
          .add(1, "days")
          .hours(0)
          .minute(59)
          .second(59)
          .millisecond(0)
          .toDate()
          .getTime() / 1000
      : moment(date)
          .add(1, "days")
          .hours(4)
          .minute(0)
          .second(0)
          .millisecond(0)
          .toDate()
          .getTime() / 1000;
  }

  getAdditionalsPicture(request: ProgramArticlePictureRequest) {
    const url = "ProgramArticle/AdditionalsPicture";
    return this.apiService.post(url, request).map((res) => {
      return res.values.map((value) => new additionalPicture(value));
    });
  }

  saveMaxAdditionalsPicture(request: additionalPictureRequest) {
    return this.apiService.post(
      "ProgramArticle/Save/AdditionalsPicture",
      request
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/services/program-article.service.ts